import type { ProposalOrder } from '@moment/horizon-data-access/proposal'
import type {
  GetQuickFilterTextParams,
  ICellRendererParams,
} from 'ag-grid-community'
import type { ColumnDefinition } from '@/components/atomic/table/types'

import { round } from 'lodash'
// biome-ignore lint/correctness/noUnusedImports: Bulk suppression as part of biome migration
import React from 'react'

import Button, { ButtonVariant } from '@/components/atomic/button'
import ClickToCopy from '@/components/atomic/click-to-copy'
import Icon, { IconName } from '@/components/atomic/icon'
import {
  DATE_COLUMN,
  DURATION_COLUMN,
  RATING_COLUMN,
  TEXT_COLUMN,
} from '@/components/atomic/table/columns'
import { BodySubtext, BodyXS } from '@/components/atomic/text'
import { OrderSide } from '@/components/molecules/badges/order-side'
import { PROPOSAL_FORMATTING } from '@/portfolio/proposal-builder/formatting-constants'
import { AccountDetailsHoldingsTableColumnId } from '@/types/enums/accounts'
import { getPrincipalDecimal } from '@/utils/order-calculations'
import { formatQuantity } from '@/utils/presentation/bonds'
import {
  formatMoney,
  formatPercentage,
  isNullish,
  PLACEHOLDER,
} from '@/utils/presentation/formatting'

const holdingsColumnDefinitions = (
  identifierPreference: 'CUSIP' | 'ISIN' = 'CUSIP',
): Map<AccountDetailsHoldingsTableColumnId, ColumnDefinition> =>
  new Map([
    [
      AccountDetailsHoldingsTableColumnId.PROPOSED_ACTION,
      {
        colId: AccountDetailsHoldingsTableColumnId.PROPOSED_ACTION,
        headerName: 'Proposed action',
        field: 'proposed_action',
        hide: false,
        cellRenderer: (params: ICellRendererParams) => {
          return <OrderSide side={params.data.proposed_action} />
        },
        minWidth: 100,
      },
    ],
    [
      AccountDetailsHoldingsTableColumnId.DESCRIPTION,
      {
        headerName: 'Description',
        field: 'description',
        hide: false,
        minWidth: 300,
        colId: AccountDetailsHoldingsTableColumnId.DESCRIPTION,
        cellRenderer: (params: ICellRendererParams) => {
          const description = params.data?.description
          const cusip = params.data?.cusip
          const isin = params.data?.isin
          const instrument_id = identifierPreference === 'CUSIP' ? cusip : isin
          return (
            <div className="flex flex-col">
              <BodySubtext className="truncate">{description}</BodySubtext>
              {instrument_id ? (
                <BodyXS className="text-secondary">
                  <ClickToCopy value={instrument_id}>
                    <span className="tabular-nums">{instrument_id}</span>
                  </ClickToCopy>
                </BodyXS>
              ) : null}
            </div>
          )
        },
        getQuickFilterText: (params: GetQuickFilterTextParams) => {
          const cusip = params.data?.cusip
          const isin = params.data?.isin
          if (cusip) {
            return `${params.data.description} ${cusip}`
          }
          if (isin) {
            return `${params.data.description} ${isin}`
          }
          return params.data.description
        },
      },
    ],
    [
      AccountDetailsHoldingsTableColumnId.MATURITY_DATE,
      DATE_COLUMN({
        hide: false,
        colId: AccountDetailsHoldingsTableColumnId.MATURITY_DATE,
        field: 'maturity_date',
        headerName: 'Maturity date',
        minWidth: 120,
      }),
    ],
    [
      AccountDetailsHoldingsTableColumnId.DURATION,
      DURATION_COLUMN({
        hide: false,
        colId: AccountDetailsHoldingsTableColumnId.DURATION,
        field: 'duration',
        minWidth: 110,
        cellClass: 'ag-right-aligned-cell tabular-nums flex justify-end pl-1',
      }),
    ],
    [
      AccountDetailsHoldingsTableColumnId.SP_RATING,
      RATING_COLUMN({
        hide: false,
        colId: AccountDetailsHoldingsTableColumnId.SP_RATING,
        field: 'sp_rating',
      }),
    ],
    [
      AccountDetailsHoldingsTableColumnId.SECTOR,
      TEXT_COLUMN({
        hide: false,
        colId: AccountDetailsHoldingsTableColumnId.SECTOR,
        field: 'sector',
        headerName: 'Sector',
        minWidth: 150,
      }),
    ],
    [
      AccountDetailsHoldingsTableColumnId.CURRENT_QUANTITY,
      {
        headerName: 'Current Amount/Qty',
        field: 'current_open_quantity',
        hide: false,
        minWidth: 130,
        cellClass:
          'ag-right-aligned-cell tabular-nums flex justify-end border-left',
        headerClass: 'ag-right-aligned-header',
        colId: AccountDetailsHoldingsTableColumnId.CURRENT_QUANTITY,
        cellRenderer: (params: ICellRendererParams) => {
          const totalMarketValue = getPrincipalDecimal({
            quantity: params.data.current_open_quantity,
            price: params.data.current_price,
          })

          return (
            <div className="flex flex-col">
              <BodySubtext>
                {formatMoney(Number(totalMarketValue), {
                  numDigits: 2,
                  useZeroPlaceholder: true,
                })}
              </BodySubtext>
              <BodyXS className="text-secondary">
                {formatQuantity(params.data.current_open_quantity)}
              </BodyXS>
            </div>
          )
        },
      },
    ],
    [
      AccountDetailsHoldingsTableColumnId.OPEN_QUANTITY,
      {
        headerName: 'Proposed Amount/Qty',
        field: 'open_quantity',
        hide: false,
        minWidth: 130,
        cellClass: 'ag-right-aligned-cell tabular-nums flex justify-end',
        headerClass: 'ag-right-aligned-header',
        colId: AccountDetailsHoldingsTableColumnId.OPEN_QUANTITY,
        cellRenderer: (params: ICellRendererParams) => {
          const totalMarketValue = getPrincipalDecimal({
            quantity: params.data.open_quantity,
            price: params.data.current_price,
          })

          return (
            <div className="flex flex-col">
              <BodySubtext>
                {formatMoney(Number(totalMarketValue), {
                  numDigits: 2,
                  useZeroPlaceholder: true,
                })}
              </BodySubtext>
              <BodyXS className="text-secondary">
                {formatQuantity(params.data.open_quantity)}
              </BodyXS>
            </div>
          )
        },
      },
    ],
    [
      AccountDetailsHoldingsTableColumnId.LIMIT_PRICE,
      {
        headerName: 'Limit price/YTW',
        field: 'limit_price',
        hide: false,
        minWidth: 130,
        cellClass: 'ag-right-aligned-cell tabular-nums flex justify-end',
        headerClass: 'ag-right-aligned-header',
        colId: AccountDetailsHoldingsTableColumnId.LIMIT_PRICE,
        cellRenderer: (params: ICellRendererParams) => {
          // Handle null limit price/YTW for holdings without orders
          if (isNullish(params.data.limit_price)) {
            return PLACEHOLDER
          }

          const formattedYTW =
            params.data.limit_ytw === 0
              ? PLACEHOLDER
              : formatPercentage(params.data.limit_ytw, {
                  numDigits: PROPOSAL_FORMATTING.YTW_DECIMALS,
                  useZeroPlaceholder: true,
                })
          return (
            <div className="flex flex-col">
              <BodySubtext>
                {formatMoney(params.data.limit_price, {
                  numDigits: PROPOSAL_FORMATTING.PRICE_DECIMALS,
                  useZeroPlaceholder: true,
                })}
              </BodySubtext>
              <BodyXS className="text-secondary">{formattedYTW}</BodyXS>
            </div>
          )
        },
      },
    ],
    [
      AccountDetailsHoldingsTableColumnId.EVAL_PRICE,
      {
        headerName: 'Eval mid price/YTW',
        field: 'eval_price',
        hide: false,
        minWidth: 130,
        cellClass: 'ag-right-aligned-cell tabular-nums flex justify-end',
        headerClass: 'ag-right-aligned-header',
        colId: AccountDetailsHoldingsTableColumnId.EVAL_PRICE,
        cellRenderer: (params: ICellRendererParams) => {
          return (
            <div className="flex flex-col">
              <BodySubtext>
                {formatMoney(params.data.eval_price, {
                  numDigits: PROPOSAL_FORMATTING.PRICE_DECIMALS,
                  useZeroPlaceholder: true,
                })}
              </BodySubtext>
              <BodyXS className="text-secondary">
                {formatPercentage(params.data.eval_ytw, {
                  numDigits: PROPOSAL_FORMATTING.YTW_DECIMALS,
                  useZeroPlaceholder: true,
                })}
              </BodyXS>
            </div>
          )
        },
      },
    ],
    [
      AccountDetailsHoldingsTableColumnId.PRICE_DIFFERENCE,
      {
        headerName: 'Difference',
        field: 'price_difference',
        minWidth: 100,
        sortable: false,
        hide: false,
        cellClass: 'ag-right-aligned-cell tabular-nums flex justify-end w-full',
        headerClass: 'ag-right-aligned-header',
        colId: AccountDetailsHoldingsTableColumnId.PRICE_DIFFERENCE,
        cellRenderer: (params: ICellRendererParams) => {
          // Only show difference if we have a limit price
          if (isNullish(params.data.limit_price)) {
            return PLACEHOLDER
          }

          const priceDifference =
            round(params.data.limit_price, PROPOSAL_FORMATTING.PRICE_DECIMALS) -
            round(params.data.eval_price, PROPOSAL_FORMATTING.PRICE_DECIMALS)
          const yieldDifference =
            round(params.data.limit_ytw, PROPOSAL_FORMATTING.YTW_DECIMALS) -
            round(params.data.eval_ytw, PROPOSAL_FORMATTING.YTW_DECIMALS)

          return (
            <div className="flex flex-col">
              <BodySubtext>
                {priceDifference > 0 ? '+' : ''}
                {formatMoney(priceDifference, {
                  numDigits: PROPOSAL_FORMATTING.PRICE_DECIMALS,
                  useZeroPlaceholder: true,
                })}
              </BodySubtext>
              <BodyXS className="text-secondary">
                {yieldDifference > 0 ? '+' : ''}
                {formatPercentage(yieldDifference, {
                  numDigits: PROPOSAL_FORMATTING.YTW_DECIMALS,
                  useZeroPlaceholder: true,
                })}
              </BodyXS>
            </div>
          )
        },
      },
    ],
    [
      AccountDetailsHoldingsTableColumnId.TAX_EFFECTIVE_LIMIT_YTW,
      {
        headerName: 'TEY',
        field: 'tax_effective_limit_ytw',
        hide: false,
        minWidth: 110,
        cellClass: 'ag-right-aligned-cell tabular-nums flex justify-end',
        headerClass: 'ag-right-aligned-header',
        colId: AccountDetailsHoldingsTableColumnId.TAX_EFFECTIVE_LIMIT_YTW,
        cellRenderer: (params: ICellRendererParams) => {
          const taxEffectiveLimitYtw = params.data.tax_effective_limit_ytw

          if (isNullish(taxEffectiveLimitYtw) || taxEffectiveLimitYtw === 0) {
            return PLACEHOLDER
          }

          return (
            <BodySubtext>
              {formatPercentage(taxEffectiveLimitYtw, {
                numDigits: PROPOSAL_FORMATTING.YTW_DECIMALS,
                useZeroPlaceholder: true,
              })}
            </BodySubtext>
          )
        },
      },
    ],
    [
      AccountDetailsHoldingsTableColumnId.POST_TAX_LIMIT_YTW,
      {
        headerName: 'Post-tax YTW',
        field: 'post_tax_limit_ytw',
        hide: false,
        minWidth: 110,
        cellClass: 'ag-right-aligned-cell tabular-nums flex justify-end',
        headerClass: 'ag-right-aligned-header',
        colId: AccountDetailsHoldingsTableColumnId.POST_TAX_LIMIT_YTW,
        cellRenderer: (params: ICellRendererParams) => {
          const postTaxLimitYtw = params.data.post_tax_limit_ytw

          if (isNullish(postTaxLimitYtw) || postTaxLimitYtw === 0) {
            return PLACEHOLDER
          }

          return (
            <BodySubtext>
              {formatPercentage(postTaxLimitYtw, {
                numDigits: PROPOSAL_FORMATTING.YTW_DECIMALS,
                useZeroPlaceholder: true,
              })}
            </BodySubtext>
          )
        },
      },
    ],
    [
      AccountDetailsHoldingsTableColumnId.REMOVE_ORDER,
      {
        headerName: '',
        hide: false,
        minWidth: 150,
        resizable: false,
        colId: AccountDetailsHoldingsTableColumnId.REMOVE_ORDER,
        context: {
          handleOrderRemoveAction: undefined,
          handleOrderSwapAction: undefined,
          handleOrderEditAction: undefined,
        },
        cellRenderer: (params: ICellRendererParams) => {
          // Only show actions if this holding has a corresponding order
          if (!params.data.corresponding_order) {
            return null
          }

          const handleOrderRemoveAction = params.colDef?.context
            ?.handleOrderRemoveAction as (order: ProposalOrder) => void
          const handleOrderSwapAction = params.colDef?.context
            ?.handleOrderSwapAction as (order: ProposalOrder) => void
          const handleOrderEditAction = params.colDef?.context
            ?.handleOrderEditAction as (order: ProposalOrder) => void

          if (
            !handleOrderRemoveAction ||
            !handleOrderSwapAction ||
            !handleOrderEditAction
          ) {
            return null
          }

          const isBuyAction = params.data.proposed_action === 'buy'

          return (
            <div className="w-full flex items-center justify-end gap-2 pr-8">
              {isBuyAction && (
                <Button
                  icon={
                    <Icon
                      iconName={IconName.ArrowPathIcon}
                      outline
                      size="medium"
                      className="text-moment-secondary"
                    />
                  }
                  onClick={() =>
                    handleOrderSwapAction(params.data.corresponding_order)
                  }
                  variant={ButtonVariant.Secondary}
                />
              )}
              <Button
                icon={
                  <Icon
                    iconName={IconName.PencilIcon}
                    outline
                    size="medium"
                    className="text-moment-secondary"
                  />
                }
                onClick={() =>
                  handleOrderEditAction(params.data.corresponding_order)
                }
                variant={ButtonVariant.Secondary}
              />
              <Button
                icon={
                  <Icon
                    iconName={IconName.MinusCircleIcon}
                    className="text-moment-red-700 dark:text-moment-red-400"
                    outline={true}
                  />
                }
                variant={ButtonVariant.Secondary}
                onClick={() =>
                  handleOrderRemoveAction(params.data.corresponding_order)
                }
              />
            </div>
          )
        },
      },
    ],
  ])

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default holdingsColumnDefinitions
